<template>
  <modal name="securone-import" class="modal-inner" transition="pop-out" :width="760" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" @before-close="reset">
    <span class="close-button" @click="$modal.hide('securone-import')"><BaseIcon icon="times-circle" class="text-white" /></span>
    <div class="modal-header text-lg font-serif">{{ $t("securone.import.title") }}</div>
    <div class="modal-body">
      <!-- ============ result ============ -->
      <template v-if="result">
        <SoNote :tone="result.created.length ? 'good' : 'warn'" class="mb-3">
          <b class="font-semibold">{{ $tc("securone.import.created_count", result.created.length, { n: result.created.length }) }}</b>
          {{ $t("securone.import.created_help", { department: result.departmentNames.join(", ") }) }}
        </SoNote>
        <template v-if="result.skipped.length">
          <div class="text-sm font-semibold text-secondary-800 mb-1">{{ $tc("securone.import.skipped_count", result.skipped.length, { n: result.skipped.length }) }}</div>
          <div class="border border-secondary-200 rounded overflow-auto" style="max-height: 260px">
            <table class="sd-table w-full text-sm">
              <thead><tr><th class="text-left">{{ $t("securone.import.col_row") }}</th><th class="text-left">{{ $t("securone.invite.email") }}</th><th class="text-left">{{ $t("securone.import.col_reason") }}</th></tr></thead>
              <tbody>
                <tr v-for="s in result.skipped" :key="s.row" class="border-t border-secondary-100"><td class="px-3 py-1.5 font-mono text-xs">{{ s.row }}</td><td class="px-3 py-1.5">{{ s.email || "—" }}</td><td class="px-3 py-1.5 text-secondary-600">{{ $t("securone.import.reasons." + s.reason) }}</td></tr>
              </tbody>
            </table>
          </div>
        </template>
      </template>

      <!-- ============ file + preview + settings ============ -->
      <template v-else>
        <SoNote tone="info" class="mb-3">{{ $t("securone.import.intro") }}</SoNote>

        <label class="flex items-center gap-3 rounded border border-dashed border-secondary-300 bg-secondary-50 px-4 py-3 cursor-pointer hover:bg-secondary-100">
          <BaseIcon icon="file-excel" class="text-secondary-500 text-lg" />
          <span class="text-sm text-secondary-700 flex-1 min-w-0 truncate">{{ fileName || $t("securone.import.choose_file") }}</span>
          <span class="sd-btn sd-btn--secondary sd-btn--sm">{{ $t("securone.import.browse") }}</span>
          <input type="file" class="hidden" accept=".xlsx,.xls,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" @change="onFile" />
        </label>
        <p class="so-hint">{{ $t("securone.import.columns_help") }}</p>

        <SoNote v-if="parseError" tone="bad" class="mt-3">{{ parseError }}</SoNote>

        <template v-if="rows.length">
          <!-- detected columns -->
          <div class="flex flex-wrap gap-1.5 mt-3">
            <SoChip v-for="(col, field) in mapping" :key="field" :tone="col ? 'blue' : 'plain'">{{ $t("securone.import.fields." + field) }}: {{ col || "—" }}</SoChip>
          </div>

          <!-- preview -->
          <div class="flex items-center justify-between mt-3 mb-1">
            <div class="text-sm font-semibold text-secondary-800">{{ $t("securone.import.preview") }}</div>
            <div class="text-xs text-secondary-500">{{ $tc("securone.import.rows_summary", rows.length, { n: rows.length, ok: valid.length, bad: rows.length - valid.length }) }}</div>
          </div>
          <div class="border border-secondary-200 rounded overflow-auto">
            <table class="sd-table w-full text-sm">
              <thead><tr><th class="text-left">{{ $t("securone.invite.first_name") }}</th><th class="text-left">{{ $t("securone.invite.last_name") }}</th><th class="text-left">{{ $t("securone.invite.email") }}</th><th class="text-left">{{ $t("securone.invite.phone") }}</th><th></th></tr></thead>
              <tbody>
                <tr v-for="r in preview" :key="r.row" class="border-t border-secondary-100" :class="{ 'text-secondary-400': r.problem }">
                  <td class="px-3 py-1.5">{{ r.firstname || "—" }}</td>
                  <td class="px-3 py-1.5">{{ r.lastname || "—" }}</td>
                  <td class="px-3 py-1.5">{{ r.email || "—" }}</td>
                  <td class="px-3 py-1.5">{{ r.phone || "—" }}</td>
                  <td class="px-3 py-1.5 text-xs text-red-600">{{ r.problem ? $t("securone.import.reasons." + r.problem) : "" }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="rows.length > preview.length" class="px-3 py-1.5 text-xs text-secondary-500 bg-secondary-50 border-t border-secondary-100">{{ $t("securone.import.and_more", { n: rows.length - preview.length }) }}</div>
          </div>

          <!-- department + groups for everyone -->
          <div class="mt-4">
            <DepartmentPicker ref="departments" v-model="departmentIds" :single="true" :label="$t('securone.import.department_label')" :help="$t('securone.import.department_help')" />
          </div>
          <div v-if="eligibleGroups.length" class="mt-3">
            <div class="text-sm font-semibold text-secondary-800 pb-2">{{ $t("securone.import.groups_label") }}</div>
            <div class="flex flex-wrap gap-2">
              <label v-for="g in eligibleGroups" :key="g._id" class="inline-flex items-center gap-2 border rounded px-2 py-1 text-sm cursor-pointer" :class="groupIds.includes(g._id) ? 'border-secondary-400 bg-secondary-50' : 'border-secondary-300'">
                <input type="checkbox" class="form-checkbox h-4 w-4" :value="g._id" v-model="groupIds" />{{ g.name }}
              </label>
            </div>
            <p class="so-hint">{{ $t("securone.import.groups_help") }}</p>
          </div>
        </template>

        <SoNote v-if="error" tone="bad" class="mt-3">{{ error }}</SoNote>
      </template>
    </div>
    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="sd-btn sd-btn--secondary float-left" @click="$modal.hide('securone-import')">{{ result ? $t("securone.import.close") : $t("securone.common.cancel") }}<BaseIcon icon="times-circle" class="ml-1" /></button>
        <button v-if="!result" class="sd-btn sd-btn--success float-right" :disabled="busy || !valid.length || !departmentIds.length" @click="submit">
          {{ busy ? $t("securone.import.importing") : $tc("securone.import.import_button", valid.length, { n: valid.length }) }}<BaseIcon icon="file-import" class="ml-1" />
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
import XLSX from "xlsx";
import SoNote from "./SoNote.vue";
import SoChip from "./SoChip.vue";
import DepartmentPicker from "./DepartmentPicker.vue";
import api from "../securone.api";
import { errorMessage } from "../securone.helpers";

// Bulk invite of basic users from an Excel or CSV file (Marcus 2026-09-21).
// The browser parses the file; the header row decides which column is what
// (names below, case-insensitive). A single "name" column is split: the first
// word is the first name, the rest the surname (double surnames stay whole).
// Everyone lands in ONE department chosen here, optionally in groups, and gets
// the same welcome mail as a single invite. The server checks every row again.
const HEADERS = {
  firstname: ["firstname", "first name", "first_name", "förnamn", "fornamn", "fornavn", "imię", "imie", "given name", "givenname"],
  lastname: ["lastname", "last name", "last_name", "efternamn", "etternavn", "surname", "nazwisko", "family name", "familyname"],
  name: ["name", "namn", "navn", "fullname", "full name", "full_name", "imię i nazwisko", "imie i nazwisko"],
  email: ["email", "e-mail", "epost", "e-post", "mail", "mejl", "e-postadress", "emailaddress", "email address", "e-mailadres"],
  phone: ["phone", "telefon", "telefonnummer", "mobil", "mobile", "mobilnummer", "tel", "phone number", "telefonnr", "mobilnr", "nummer"],
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const MAX_ROWS = 500;

export default {
  name: "ImportUsersModal",
  components: { SoNote, SoChip, DepartmentPicker },
  props: {
    groups: { type: Array, default: () => [] },
  },
  data() {
    return { fileName: "", rows: [], mapping: {}, parseError: null, departmentIds: [], groupIds: [], busy: false, error: null, result: null };
  },
  computed: {
    valid() {
      return this.rows.filter((r) => !r.problem);
    },
    preview() {
      // the first rows, with problem rows pulled forward so they are seen
      const bad = this.rows.filter((r) => r.problem).slice(0, 3);
      const ok = this.rows.filter((r) => !r.problem).slice(0, 6 - bad.length);
      return [...ok, ...bad].sort((a, b) => a.row - b.row);
    },
    eligibleGroups() {
      // V2: a global group, or a department group touching the chosen department
      const dep = this.departmentIds[0] ? String(this.departmentIds[0]) : null;
      return this.groups.filter((g) => g.canEdit !== false && (g.scope === "global" || (dep && (g.departmentIds || []).map(String).includes(dep))));
    },
  },
  methods: {
    open() {
      this.reset();
      this.$modal.show("securone-import");
    },
    reset() {
      this.fileName = "";
      this.rows = [];
      this.mapping = {};
      this.parseError = null;
      this.departmentIds = [];
      this.groupIds = [];
      this.busy = false;
      this.error = null;
      this.result = null;
    },
    onFile(e) {
      const file = e.target.files && e.target.files[0];
      e.target.value = "";
      if (!file) return;
      this.fileName = file.name;
      this.parseError = null;
      this.rows = [];
      const reader = new FileReader();
      reader.onload = (evt) => {
        try {
          const workbook = XLSX.read(evt.target.result, { type: "binary", codepage: 65001 });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const raw = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
          this.parse(raw);
        } catch (err) {
          this.parseError = this.$t("securone.import.parse_failed");
        }
      };
      reader.readAsBinaryString(file);
    },
    norm(h) {
      return String(h || "").trim().toLowerCase().replace(/\s+/g, " ");
    },
    parse(raw) {
      if (!raw.length) {
        this.parseError = this.$t("securone.import.empty_file");
        return;
      }
      const headers = Object.keys(raw[0]);
      const mapping = {};
      for (const field of Object.keys(HEADERS)) {
        mapping[field] = headers.find((h) => HEADERS[field].includes(this.norm(h))) || null;
      }
      this.mapping = mapping;
      if (!mapping.email || (!mapping.firstname && !mapping.name)) {
        this.parseError = this.$t("securone.import.columns_missing", { found: headers.join(", ") });
        return;
      }
      if (raw.length > MAX_ROWS) {
        this.parseError = this.$t("securone.import.too_many", { max: MAX_ROWS, n: raw.length });
        return;
      }
      const seen = new Set();
      this.rows = raw.map((r, i) => {
        let firstname = mapping.firstname ? String(r[mapping.firstname] || "").trim() : "";
        let lastname = mapping.lastname ? String(r[mapping.lastname] || "").trim() : "";
        if (!firstname && mapping.name) {
          const parts = String(r[mapping.name] || "").trim().split(/\s+/).filter(Boolean);
          firstname = parts.shift() || "";
          lastname = lastname || parts.join(" ");
        }
        const email = String(r[mapping.email] || "").trim().toLowerCase();
        const phone = mapping.phone ? String(r[mapping.phone] || "").trim() : "";
        let problem = null;
        if (!firstname && !email) problem = "empty_row";
        else if (!firstname) problem = "missing_name";
        else if (!EMAIL_RE.test(email)) problem = "invalid_email";
        else if (seen.has(email)) problem = "duplicate_in_file";
        if (!problem) seen.add(email);
        return { row: i + 2, firstname, lastname, email, phone, problem }; // row 1 is the header
      }).filter((r) => r.problem !== "empty_row");
      if (!this.rows.length) this.parseError = this.$t("securone.import.empty_file");
    },
    async submit() {
      if (!this.departmentIds.length) {
        if (this.$refs.departments) this.$refs.departments.touched = true;
        return;
      }
      this.busy = true;
      this.error = null;
      try {
        const rows = this.valid.map((r) => ({ firstname: r.firstname, lastname: r.lastname, email: r.email, phone: r.phone }));
        this.result = await api.importUsers({ departmentIds: this.departmentIds, groups: this.groupIds, rows });
        this.$emit("imported", this.result);
      } catch (error) {
        this.error = errorMessage(this, error);
      } finally {
        this.busy = false;
      }
    },
  },
  watch: {
    departmentIds() {
      const ok = this.eligibleGroups.map((g) => g._id);
      this.groupIds = this.groupIds.filter((id) => ok.includes(id));
    },
  },
};
</script>
